'use client'

import * as React from 'react'
import { ENGLISH_MESSAGES, loadMessages } from '@/lib/i18n/catalogues'
import { directionFor } from '@/lib/i18n/direction'
import { readStoredLocale } from '@/lib/i18n/locale-preference'
import { richText } from '@/lib/i18n/rich-text'
import { translate } from '@/lib/i18n/translate'
import type { Locale, Messages } from '@/lib/i18n/types'
import { STATUS_ARTWORK_SIZE, statusArtworkSrc, statusHomeHref } from '@/lib/status/status-artwork'

/**
 * What app/global-error.tsx shows: the product's 500 page for a crash IN the
 * root layout. Nothing the app normally leans on is there — no providers, no
 * globals.css, no Tailwind, no theme, no router to trust — so this brings its
 * own styles (light and dark from the system, right to left from the
 * language), its own picture, and reads the saved language itself.
 */

interface Catalogue {
  locale: Locale
  messages: Messages
}

/**
 * The reader's saved language (localStorage, then the cookie) and its
 * catalogue. English until the catalogue arrives: it is bundled, and a crash
 * page must never wait on a fetch to say something.
 */
export function useStoredLocaleCatalogue(): Catalogue {
  const [catalogue, setCatalogue] = React.useState<Catalogue>({ locale: 'en', messages: ENGLISH_MESSAGES })
  React.useEffect(() => {
    const stored = readStoredLocale()
    if (!stored || stored === 'en') return
    let live = true
    loadMessages(stored)
      .then((messages) => {
        if (live) setCatalogue({ locale: stored, messages })
      })
      .catch(() => {
        // The catalogue could not load (offline, a chunk gone under a deploy): English it is.
      })
    return () => {
      live = false
    }
  }, [])
  return catalogue
}

const NO_SUBSCRIBE = () => () => {}

/** Self-contained: the page has no stylesheet but this one. */
const GLOBAL_ERROR_CSS = `
.hf-ge{box-sizing:border-box;min-height:100vh;margin:0;display:flex;align-items:center;justify-content:center;padding:48px 16px;background:oklch(1 0 0);color:oklch(0.145 0 0);font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,"Noto Sans","Noto Sans Arabic","Noto Sans Devanagari",sans-serif;text-align:center;-webkit-font-smoothing:antialiased}
.hf-ge *,.hf-ge *::before,.hf-ge *::after{box-sizing:border-box}
.hf-ge-inner{width:100%;max-width:36rem;display:flex;flex-direction:column;align-items:center}
.hf-ge-frame{position:relative;isolation:isolate;width:100%;max-width:26rem;padding:8px;border-radius:32px;background:oklch(1 0 0 / .8);box-shadow:0 0 0 1px oklch(0 0 0 / .05),0 1px 2px oklch(0 0 0 / .05),0 24px 48px -20px oklch(0 0 0 / .25)}
.hf-ge-frame img{display:block;width:100%;height:auto;aspect-ratio:4/3;object-fit:cover;border-radius:24px}
.hf-ge-code{margin-top:32px;border:1px solid oklch(0.922 0 0);border-radius:999px;padding:4px 12px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:12px;font-weight:500;color:oklch(0.556 0 0)}
.hf-ge-title{margin:12px 0 0;font-size:30px;line-height:1.2;font-weight:600;text-wrap:balance}
.hf-ge-body{margin:12px 0 0;max-width:28rem;font-size:16px;line-height:1.625;color:oklch(0.556 0 0);text-wrap:pretty}
.hf-ge-actions{margin-top:32px;display:flex;flex-wrap:wrap;gap:12px;justify-content:center}
.hf-ge-button{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-width:10rem;height:40px;padding:0 24px;border-radius:10px;border:1px solid oklch(0.922 0 0);background:oklch(1 0 0);color:oklch(0.145 0 0);font-family:inherit;font-size:14px;font-weight:500;text-decoration:none;cursor:pointer}
.hf-ge-button:focus-visible{outline:3px solid oklch(0.708 0 0 / .5);outline-offset:2px}
.hf-ge-primary{background:oklch(0.205 0 0);border-color:oklch(0.205 0 0);color:oklch(0.985 0 0)}
.hf-ge-primary:hover{background:oklch(0.205 0 0 / .9)}
.hf-ge-button:not(.hf-ge-primary):hover{background:oklch(0.97 0 0)}
.hf-ge-reference{margin-top:24px;font-size:12px;line-height:1.6;color:oklch(0.556 0 0)}
.hf-ge-reference code{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;padding:2px 6px;border-radius:4px;background:oklch(0.97 0 0);color:oklch(0.145 0 0);user-select:all}
@media (prefers-color-scheme: dark){
.hf-ge{background:oklch(0.145 0 0);color:oklch(0.985 0 0)}
.hf-ge-frame{background:oklch(1 0 0 / .04);box-shadow:0 0 0 1px oklch(1 0 0 / .1),0 24px 48px -20px oklch(0 0 0 / .6)}
.hf-ge-frame img{filter:brightness(.92)}
.hf-ge-code{border-color:oklch(1 0 0 / .1);color:oklch(0.708 0 0)}
.hf-ge-body,.hf-ge-reference{color:oklch(0.708 0 0)}
.hf-ge-button{background:oklch(1 0 0 / .045);border-color:oklch(1 0 0 / .15);color:oklch(0.985 0 0)}
.hf-ge-button:not(.hf-ge-primary):hover{background:oklch(1 0 0 / .1)}
.hf-ge-primary{background:oklch(0.922 0 0);border-color:oklch(0.922 0 0);color:oklch(0.205 0 0)}
.hf-ge-primary:hover{background:oklch(0.922 0 0 / .9)}
.hf-ge-reference code{background:oklch(1 0 0 / .08);color:oklch(0.985 0 0)}
}
@media (max-width:640px){
.hf-ge-title{font-size:24px}
.hf-ge-actions{flex-direction:column;width:100%}
.hf-ge-button{width:100%}
}
@media (prefers-reduced-motion: no-preference){
.hf-ge-inner{animation:hf-ge-in .6s ease-out both}
@keyframes hf-ge-in{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
}`

export function GlobalErrorView({
  locale,
  messages,
  digest,
  onRetry,
}: {
  locale: Locale
  messages: Messages
  digest?: string
  /** "Try again". Without it the page reloads: after a root crash a fresh load is the reliable retry. */
  onRetry?: () => void
}) {
  const t = (key: string, params?: Record<string, string | number>) =>
    translate(messages, ENGLISH_MESSAGES, key, params, { locale })
  const pathname = React.useSyncExternalStore(
    NO_SUBSCRIBE,
    () => window.location.pathname,
    () => null,
  )
  const src = statusArtworkSrc('500')
  return (
    <main data-testid="global-error-view" className="hf-ge" lang={locale} dir={directionFor(locale)}>
      <style>{GLOBAL_ERROR_CSS}</style>
      <div className="hf-ge-inner">
        <div className="hf-ge-frame">
          {/* eslint-disable-next-line @next/next/no-img-element -- no image optimiser to rely on under the root boundary */}
          <img
            src={src}
            alt={t('shell.status.500.alt')}
            width={STATUS_ARTWORK_SIZE.width}
            height={STATUS_ARTWORK_SIZE.height}
            loading="eager"
            decoding="async"
          />
        </div>
        <span className="hf-ge-code">{t('shell.status.code', { code: '500' })}</span>
        <h1 className="hf-ge-title">{t('shell.status.500.title')}</h1>
        <p className="hf-ge-body">{t('shell.status.500.body')}</p>
        <div className="hf-ge-actions">
          <button
            type="button"
            className="hf-ge-button hf-ge-primary"
            onClick={onRetry ?? (() => window.location.reload())}
          >
            {t('shell.status.500.primary')}
          </button>
          {/* A full page load, on purpose: the client router is what may have broken. */}
          <a className="hf-ge-button" href={statusHomeHref(pathname)}>
            {t('shell.status.500.secondary')}
          </a>
        </div>
        {digest ? (
          <p className="hf-ge-reference">
            {richText(t('shell.status.reference'), { digest: <code dir="ltr">{digest}</code> })}
          </p>
        ) : null}
      </div>
    </main>
  )
}
